import type { RecordOf } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import { investmentQuotesV2Actions } from 'app-components/flux/investment-quotes-v2';

import type { AsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { accountsActions } from '../accounts';
import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import type { QcsSyncResourceStoreProps } from '../core/resourceStoreTypes';
import * as actions from './investmentHoldingsV2Actions';
import * as investmentHoldingsV2Selectors from './investmentHoldingsV2Selectors';
import { type InvestmentHoldingV2Type, INVESTMENT_HOLDINGS_V2_REDUCER_KEY as KEY } from './investmentHoldingsV2Types';

type State = RecordOf<QcsSyncResourceStoreProps<InvestmentHoldingV2Type>>;

const InvestmentHoldingsV2StoreFactory =
  resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<InvestmentHoldingV2Type>();

const investmentHoldingsV2Reducer = handleActions<
  State,
  | ReturnType<typeof actions.createInvestmentHoldingV2>
  | ReturnType<typeof actions.createInvestmentHoldingV2Success>
  | ReturnType<typeof actions.updateInvestmentHoldingV2>
  | ReturnType<typeof actions.updateInvestmentHoldingV2Success>
  | ReturnType<typeof actions.batchInvestmentHoldingsV2>
  | ReturnType<typeof actions.batchInvestmentHoldingsV2Success>
  | ReturnType<typeof actions.deleteInvestmentHoldingV2>
  | ReturnType<typeof actions.deleteInvestmentHoldingV2Success>
  | ReturnType<typeof actions.getInvestmentHoldingsV2>
  | ReturnType<typeof actions.getInvestmentHoldingsV2Success>
  | ReturnType<typeof actions.getInvestmentHoldingsV2Failure>
  | ReturnType<typeof accountsActions.applyAccountsChanges>
>(
  {
    [actions.createInvestmentHoldingV2.type]: (state, { payload: holding }) =>
      resourceUpserters.upsertResource<InvestmentHoldingV2Type, QcsSyncResourceStoreProps<InvestmentHoldingV2Type>>(
        state,
        holding,
      ),
    [actions.createInvestmentHoldingV2Success.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo<
        InvestmentHoldingV2Type,
        QcsSyncResourceStoreProps<InvestmentHoldingV2Type>
      >(state, action, actions.deleteInvestmentHoldingV2),

    [actions.updateInvestmentHoldingV2.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo<
        InvestmentHoldingV2Type,
        QcsSyncResourceStoreProps<InvestmentHoldingV2Type>
      >(state, action, actions.updateInvestmentHoldingV2),
    [actions.updateInvestmentHoldingV2Success.type]: (state, { payload: holding }) =>
      resourceUpserters.upsertResource<InvestmentHoldingV2Type, QcsSyncResourceStoreProps<InvestmentHoldingV2Type>>(
        state,
        holding,
      ),

    [actions.batchInvestmentHoldingsV2.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(
        state,
        { ...action, payload: Array.from(action.payload.values()) },
        actions.batchInvestmentHoldingsV2,
        false,
      ),
    [actions.batchInvestmentHoldingsV2Success.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchInvestmentHoldingsV2, true),

    [actions.deleteInvestmentHoldingV2.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo<
        InvestmentHoldingV2Type,
        QcsSyncResourceStoreProps<InvestmentHoldingV2Type>
      >(state, action, actions.createInvestmentHoldingV2),
    [actions.deleteInvestmentHoldingV2Success.type]: (state, { payload: holding }) =>
      resourceUpserters.upsertResource<InvestmentHoldingV2Type, QcsSyncResourceStoreProps<InvestmentHoldingV2Type>>(
        state,
        holding,
      ),

    [combineActions(
      actions.batchInvestmentHoldingsV2Failure,
      actions.createInvestmentHoldingV2Failure,
      actions.updateInvestmentHoldingV2Failure,
      actions.deleteInvestmentHoldingV2Failure,
    ).toString()]: (
      state: State,
      { payload: error, asyncDispatch }: { payload: unknown; asyncDispatch: AsyncDispatch },
    ) => {
      asyncDispatch(actions.getInvestmentHoldingsV2());
      return resourceUpserters.resetResources<
        InvestmentHoldingV2Type,
        QcsSyncResourceStoreProps<InvestmentHoldingV2Type>
      >(state, error);
    },

    [actions.getInvestmentHoldingsV2.type]: (state) => state.merge({ isLoading: true }),
    [actions.getInvestmentHoldingsV2Success.type]: (state, action) => {
      const investmentHoldings = resourceUpserters.upsertResources<
        InvestmentHoldingV2Type,
        QcsSyncResourceStoreProps<InvestmentHoldingV2Type>
      >(state, action.payload);
      const symbols = investmentHoldingsV2Selectors.getSymbols(undefined, investmentHoldings.resourcesById);
      if (symbols.size) {
        action.asyncDispatch(
          investmentQuotesV2Actions.getInvestmentQuotesV2(undefined, {
            axiosConfig: { params: { symbols: symbols.join(',') } },
          }),
        );
      }
      return investmentHoldings;
    },
    [actions.getInvestmentHoldingsV2Failure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError<InvestmentHoldingV2Type, QcsSyncResourceStoreProps<InvestmentHoldingV2Type>>(
        state,
        error,
      ),

    [accountsActions.applyAccountsChanges.type]: (state, { payload, asyncDispatch }) => {
      const deletedAccountIds = payload.filter((account) => account.isDeleted).map((account) => account.id);
      if (deletedAccountIds.length) {
        asyncDispatch(actions.getInvestmentHoldingsV2(undefined, { context: 'applyAccountsChanges' })); /// TODO: remove after bump is enabled
        const resourcesById = state.resourcesById.filter((holding) => !deletedAccountIds.includes(holding.accountId));
        return state.merge({ resourcesById });
      }
      return state;
    },
  },
  InvestmentHoldingsV2StoreFactory(),
);

export const INVESTMENT_HOLDINGS_V2_REDUCER_KEY = KEY;
export default investmentHoldingsV2Reducer;
